// frontend/src/components/TenantAdd.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ManagerSidebar from '../../../components/Sidebar/ManagerSidebar';
import './TenantAdd.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faCalendar,
    faHome,
    faMoneyBill,
    faEnvelope,
    faGlobe,
    faPassport,
    faPhone,
    faUsers,
    faChild,
    faFileUpload,
    faHandHoldingUsd,
    faMapMarkerAlt,
    faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';

const TenantAdd = () => {

    const navigate = useNavigate();

    const [tenantData, setTenantData] = useState({
        guest_name: '',
        arrival_date: '',
        departure_date: '',
        unitname: '',
        rent_usd: 0,
        rent_rwf: 0,
        deposites: 0,
        deposites_usd: 0,
        deposites_rwf: 0,
        email_address: '',
        nationalite: '',
        country_residence: '',
        passport_id: '',
        number_adults: 1,
        number_child: 0,
        mobile_number: '',
        price_per_month: 0,
        number_of_months: '',
        duration_type: 'days'
    });

    // Currency states
    const [currency, setCurrency] = useState('RWF');
    const [exchangeRate, setExchangeRate] = useState('');

    const [depositCurrency, setDepositCurrency] = useState('RWF');
    const [depositExchangeRate, setDepositExchangeRate] = useState('');

    const [passportFile, setPassportFile] = useState(null);
    const [allApartmentUnits, setAllApartmentUnits] = useState([]);

    useEffect(() => {
        fetchAllApartmentUnits();
    }, []);

    // =========================
    // FETCH APARTMENTS
    // =========================
    const fetchAllApartmentUnits = async () => {
        try {
            const res = await axios.get(
                'https://ikaze-thouse-backend.vercel.app/api/v1/appartmentUnits/getAll'
            );
            if (res.data.success) {
                setAllApartmentUnits(
                    res.data.data.filter(unit => unit.status === 'Available')
                );
            }
        } catch (err) {
            console.error(err);
        }
    };

    // =========================
    // CALCULATE DURATION
    // =========================
    const calculateDuration = (start, end) => {
        if (!start || !end) return { months: 0, extraDays: 0, totalDays: 0 };

        const startDate = new Date(start);
        const endDate = new Date(end);

        if (endDate <= startDate) return { months: 0, extraDays: 0, totalDays: 0 };

        const timeDiff = endDate.getTime() - startDate.getTime();
        const totalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

        let months =
            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
            (endDate.getMonth() - startDate.getMonth());

        if (endDate.getDate() < startDate.getDate()) months--;
        if (months < 0) months = 0;

        const tempDate = new Date(startDate);
        tempDate.setMonth(tempDate.getMonth() + months);

        const extraTimeDiff = endDate.getTime() - tempDate.getTime();
        const extraDays = Math.ceil(extraTimeDiff / (1000 * 60 * 60 * 24));

        return { months, extraDays, totalDays };
    };

    // =========================
    // COMPUTE RENT & DEPOSIT VALUES
    // =========================
    const computeRentValues = (updatedData, selectedCurrency, rate, depCurrency, depRate) => {
        const duration = calculateDuration(
            updatedData.arrival_date,
            updatedData.departure_date
        );

        const monthlyPrice = updatedData.price_per_month || 0;

        const year = new Date(updatedData.arrival_date).getFullYear();
        const month = new Date(updatedData.arrival_date).getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const dailyRate = monthlyPrice / daysInMonth;

        let rawRent = 0;

        if (duration.months >= 1) {
            updatedData.duration_type = 'months';
            updatedData.number_of_months =
                `${duration.months} Month(s) ${duration.extraDays} Day(s)`;
            rawRent = Math.round(
                duration.months * monthlyPrice + duration.extraDays * dailyRate
            );
        } else {
            updatedData.duration_type = 'days';
            updatedData.number_of_months = `${duration.totalDays} Day(s)`;
            rawRent = duration.totalDays * monthlyPrice;
        }

        // Currency split logic for Rent
        if (selectedCurrency === 'RWF') {
            updatedData.rent_rwf = rawRent;
            updatedData.rent_usd = 0;
        } else {
            const parsedRate = parseFloat(rate) || 0;
            updatedData.rent_usd = rawRent;
            updatedData.rent_rwf = parsedRate > 0
                ? Math.round(rawRent * parsedRate)
                : 0;
        }

        // Currency split logic for Deposit
        const rawDeposit = updatedData.deposites || 0;
        if (depCurrency === 'RWF') {
            updatedData.deposites_rwf = rawDeposit;
            updatedData.deposites_usd = 0;
        } else {
            const parsedDepRate = parseFloat(depRate) || 0;
            updatedData.deposites_usd = rawDeposit;
            updatedData.deposites_rwf = parsedDepRate > 0
                ? Math.round(rawDeposit * parsedDepRate)
                : 0;
        }

        return updatedData;
    };

    // =========================
    // HANDLE INPUT CHANGE
    // =========================
    const handleChange = (e) => {
        const { name, value } = e.target;

        let updatedData = {
            ...tenantData,
            [name]:
                name.includes('number') ||
                name.includes('price') ||
                name === 'deposites'
                    ? Number(value)
                    : value
        };

        updatedData = computeRentValues(updatedData, currency, exchangeRate, depositCurrency, depositExchangeRate);
        setTenantData(updatedData);
    };

    // =========================
    // HANDLE CURRENCY CHANGES
    // =========================
    const handleCurrencyChange = (e) => {
        const selected = e.target.value;
        setCurrency(selected);

        const rate = selected === 'RWF' ? '' : exchangeRate;
        setExchangeRate(rate);

        const updatedData = computeRentValues({ ...tenantData }, selected, rate, depositCurrency, depositExchangeRate);
        setTenantData(updatedData);
    };

    const handleExchangeRateChange = (e) => {
        const rate = e.target.value;
        setExchangeRate(rate);

        const updatedData = computeRentValues({ ...tenantData }, currency, rate, depositCurrency, depositExchangeRate);
        setTenantData(updatedData);
    };

    const handleDepositCurrencyChange = (e) => {
        const selected = e.target.value;
        setDepositCurrency(selected);

        const rate = selected === 'RWF' ? '' : depositExchangeRate;
        setDepositExchangeRate(rate);

        const updatedData = computeRentValues({ ...tenantData }, currency, exchangeRate, selected, rate);
        setTenantData(updatedData);
    };

    const handleDepositExchangeRateChange = (e) => {
        const rate = e.target.value;
        setDepositExchangeRate(rate);

        const updatedData = computeRentValues({ ...tenantData }, currency, exchangeRate, depositCurrency, rate);
        setTenantData(updatedData);
    };

    // =========================
    // HANDLE FILE CHANGE
    // =========================
    const handleFileChange = (e) => {
        setPassportFile(e.target.files[0]);
    };

    // =========================
    // SAVE TENANT
    // =========================
    const handleSave = async () => {
        if (!tenantData.unitname) return alert('Select unit');
        if (!tenantData.arrival_date || !tenantData.departure_date)
            return alert('Select dates');
        if (currency === 'USD' && (!exchangeRate || parseFloat(exchangeRate) <= 0))
            return alert('Enter a valid exchange rate for USD rent');
        if (depositCurrency === 'USD' && (!depositExchangeRate || parseFloat(depositExchangeRate) <= 0))
            return alert('Enter a valid exchange rate for USD deposit');

        const formData = new FormData();

        Object.keys(tenantData).forEach(key => {
            formData.append(
                key === 'unitname' ? 'appartment_unite' : key,
                tenantData[key]
            );
        });

        // Send currency and exchange rates to backend
        formData.append('currency', currency);
        formData.append('exchange_rate', currency === 'USD' ? exchangeRate : '');
        formData.append('deposit_currency', depositCurrency);
        formData.append('deposit_exchange_rate', depositCurrency === 'USD' ? depositExchangeRate : '');

        if (passportFile) {
            formData.append('passport_file', passportFile);
        }

        try {
            const res = await axios.post(
                'https://ikaze-thouse-backend.vercel.app/api/v1/tenants/create',
                formData
            );

            if (res.data.success) {
                alert('Success!');
                navigate('/manager/tenants');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to save');
        }
    };

    // =========================
    // DISPLAY SUMMARIES
    // =========================
    const rentDisplay = currency === 'RWF'
        ? `RWF ${tenantData.rent_rwf.toLocaleString()}`
        : `$ ${tenantData.rent_usd.toLocaleString()} USD  →  RWF ${tenantData.rent_rwf.toLocaleString()}`;

    const depositDisplay = depositCurrency === 'RWF'
        ? `RWF ${tenantData.deposites_rwf.toLocaleString()}`
        : `$ ${tenantData.deposites_usd.toLocaleString()} USD  →  RWF ${tenantData.deposites_rwf.toLocaleString()}`;

    return (
        <div className="add-container">
            <ManagerSidebar />

            <div className="add-tenant-form">

                {/* HEADER */}
                <div className="form-header">
                    <h2>🏠 Add New Tenant</h2>
                    <p>Enter the lease details and personal information below.</p>
                </div>

                <div className="form-grid">

                    {/* LEASE SECTION */}
                    <h3 className="section-title">Lease & Unit Details</h3>

                    {/* UNIT */}
                    <div className="input-group">
                        <label className="field-label">Apartment Unit</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faHome} className="input-icon" />
                            <select name="unitname" onChange={handleChange}>
                                <option value="">Select Unit</option>
                                {allApartmentUnits.map(unit => (
                                    <option key={unit.UnitId} value={unit.unitname}>
                                        {unit.unitname}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* ARRIVAL DATE */}
                    <div className="input-group">
                        <label className="field-label">Arrival Date</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                            <input type="date" name="arrival_date" onChange={handleChange} />
                        </div>
                    </div>

                    {/* DEPARTURE DATE */}
                    <div className="input-group">
                        <label className="field-label">Departure Date</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                            <input type="date" name="departure_date" onChange={handleChange} />
                        </div>
                    </div>

                    {/* CURRENCY SELECT */}
                    <div className="input-group">
                        <label className="field-label">Payment Currency</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faMoneyBill} className="input-icon" />
                            <select value={currency} onChange={handleCurrencyChange}>
                                <option value="RWF">RWF (Rwandan Franc)</option>
                                <option value="USD">USD (US Dollar)</option>
                            </select>
                        </div>
                    </div>

                    {/* EXCHANGE RATE — only shown for USD */}
                    {currency === 'USD' && (
                        <div className="input-group">
                            <label className="field-label">Exchange Rate (1 USD = ? RWF)</label>
                            <div className="input-wrapper">
                                <FontAwesomeIcon icon={faExchangeAlt} className="input-icon" />
                                <input
                                    type="number"
                                    placeholder="e.g. 1300"
                                    value={exchangeRate}
                                    onChange={handleExchangeRateChange}
                                    min="1"
                                />
                            </div>
                        </div>
                    )}

                    {/* PRICE */}
                    <div className="input-group">
                        <label className="field-label">
                            Price per Month / Day ({currency})
                        </label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faHandHoldingUsd} className="input-icon" />
                            <input
                                type="number"
                                name="price_per_month"
                                placeholder={`Enter price in ${currency}`}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* DURATION */}
                    <div className="input-group">
                        <label className="field-label">Duration (Auto)</label>
                        <div className="input-wrapper no-icon">
                            <input
                                value={tenantData.number_of_months}
                                readOnly
                                className="readonly-field"
                            />
                        </div>
                    </div>

                    {/* TOTAL RENT */}
                    <div className="input-group">
                        <label className="field-label">Total Rent (Auto)</label>
                        <div className="input-wrapper no-icon">
                            <input
                                value={rentDisplay}
                                readOnly
                                className="readonly-field highlight-text"
                            />
                        </div>
                    </div>

                    {/* PERSONAL INFORMATION */}
                    <h3 className="section-title">Tenant Personal Information</h3>

                    {/* FULL NAME */}
                    <div className="input-group">
                        <label className="field-label">Full Name</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faUser} className="input-icon" />
                            <input
                                type="text"
                                name="guest_name"
                                placeholder="John Doe"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* EMAIL */}
                    <div className="input-group">
                        <label className="field-label">Email Address</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                            <input
                                type="email"
                                name="email_address"
                                placeholder="mail@example.com"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* PHONE */}
                    <div className="input-group">
                        <label className="field-label">Mobile Number</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faPhone} className="input-icon" />
                            <input
                                type="text"
                                name="mobile_number"
                                placeholder="+250..."
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* PASSPORT */}
                    <div className="input-group">
                        <label className="field-label">Passport / ID</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faPassport} className="input-icon" />
                            <input
                                type="text"
                                name="passport_id"
                                placeholder="ID Number"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* NATIONALITY */}
                    <div className="input-group">
                        <label className="field-label">Nationality</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faGlobe} className="input-icon" />
                            <input
                                type="text"
                                name="nationalite"
                                placeholder="Nationality"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* COUNTRY */}
                    <div className="input-group">
                        <label className="field-label">Country</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                            <input
                                type="text"
                                name="country_residence"
                                placeholder="Country"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* OCCUPANCY & SECURITY */}
                    <h3 className="section-title">Occupancy & Security</h3>

                    {/* DEPOSIT CURRENCY SELECT */}
                    <div className="input-group">
                        <label className="field-label">Deposit Currency</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faMoneyBill} className="input-icon" />
                            <select value={depositCurrency} onChange={handleDepositCurrencyChange}>
                                <option value="RWF">RWF (Rwandan Franc)</option>
                                <option value="USD">USD (US Dollar)</option>
                            </select>
                        </div>
                    </div>

                    {/* DEPOSIT EXCHANGE RATE — only shown for USD */}
                    {depositCurrency === 'USD' && (
                        <div className="input-group">
                            <label className="field-label">Deposit Exchange Rate (1 USD = ? RWF)</label>
                            <div className="input-wrapper">
                                <FontAwesomeIcon icon={faExchangeAlt} className="input-icon" />
                                <input
                                    type="number"
                                    placeholder="e.g. 1300"
                                    value={depositExchangeRate}
                                    onChange={handleDepositExchangeRateChange}
                                    min="1"
                                />
                            </div>
                        </div>
                    )}

                    {/* DEPOSIT */}
                    <div className="input-group">
                        <label className="field-label">Security Deposit ({depositCurrency})</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faMoneyBill} className="input-icon" />
                            <input
                                type="number"
                                name="deposites"
                                placeholder="0.00"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* TOTAL DEPOSIT DISPLAY */}
                    <div className="input-group">
                        <label className="field-label">Total Deposit (Auto)</label>
                        <div className="input-wrapper no-icon">
                            <input
                                value={depositDisplay}
                                readOnly
                                className="readonly-field highlight-text"
                            />
                        </div>
                    </div>

                    {/* ADULTS */}
                    <div className="input-group">
                        <label className="field-label">Number of Adults</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faUsers} className="input-icon" />
                            <input
                                type="number"
                                name="number_adults"
                                placeholder="1"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* CHILDREN */}
                    <div className="input-group">
                        <label className="field-label">Number of Children</label>
                        <div className="input-wrapper">
                            <FontAwesomeIcon icon={faChild} className="input-icon" />
                            <input
                                type="number"
                                name="number_child"
                                placeholder="0"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* FILE */}
                    <div className="input-group file-upload-section">
                        <label className="field-label">Upload Passport/ID Document</label>
                        <div className="file-box">
                            <FontAwesomeIcon icon={faFileUpload} size="2x" className="upload-icon" />
                            <input type="file" onChange={handleFileChange} />
                            <p>
                                {passportFile
                                    ? passportFile.name
                                    : 'Drag and drop or click to upload'}
                            </p>
                        </div>
                    </div>

                </div>

                {/* BUTTONS */}
                <div className="form-buttons">
                    <button
                        className="cancel-btn"
                        onClick={() => navigate('/manager/tenants')}
                    >
                        Cancel
                    </button>
                    <button className="save-btn" onClick={handleSave}>
                        Confirm & Save
                    </button>
                </div>

            </div>
        </div>
    );
};

export default TenantAdd;